import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

import { FaSync, FaCheck, FaTimes, FaExclamationTriangle, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

interface MigrationLog {
  type: 'info' | 'success' | 'error' | 'warning';
  message: string;
  timestamp: Date;
  userId?: string;
  certificationCount?: number;
}

const CertificationMigration: React.FC = () => {
  const { currentUser } = useAuth();

  const navigate = useNavigate();
  const [isMigrating, setIsMigrating] = useState(false);
  const [logs, setLogs] = useState<MigrationLog[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [stats, setStats] = useState({ processed: 0, updated: 0, errors: 0 });

  // Verificar si el usuario es admin
  useEffect(() => {
    if (currentUser) {
      const checkAdminRole = async () => {
        const db = getFirestore();
        const userDoc = await getDocs(collection(db, 'users'));
        const user = userDoc.docs.find(doc => doc.id === currentUser.uid);
        if (user && user.data().role !== 'admin') {
          navigate('/dashboard');
        }
      };
      checkAdminRole();
    }
  }, [currentUser, navigate]);

  const addLog = (type: MigrationLog['type'], message: string, userId?: string, certificationCount?: number, additionalData?: any) => {
    let fullMessage = message;
    
    if (additionalData) {
      fullMessage += '\n' + JSON.stringify(additionalData, null, 2);
    }
    
    const newLog: MigrationLog = {
      type,
      message: fullMessage,
      timestamp: new Date(),
      userId,
      certificationCount
    };
    setLogs(prev => [...prev, newLog]);
  };

  const startMigration = async () => {
    setIsMigrating(true);
    setLogs([]);
    setProgress({ current: 0, total: 0 });
    setStats({ processed: 0, updated: 0, errors: 0 });

    try {
      const db = getFirestore();
      
      // Obtener datos de certificaciones desde Skills_and_certifications
      addLog('info', 'Obteniendo datos de certificaciones desde Skills_and_certifications...');
      const skillsSnapshot = await getDocs(collection(db, 'Skills_and_certifications'));
      const certificationData: { [key: string]: any } = {};
      
      skillsSnapshot.forEach(doc => {
        const data = doc.data();
        addLog('info', `📚 Procesando documento de Skills_and_certifications: ${doc.id}`);
        
        if (data.certifications && Array.isArray(data.certifications)) {
          addLog('info', `   Encontradas ${data.certifications.length} certificaciones en este documento`);
          
          data.certifications.forEach((cert: any, index: number) => {
            if (cert.id) {
              certificationData[cert.id] = cert;
              addLog('info', `   ✅ Cargada certificación ${index + 1}: ${cert.id}`, undefined, undefined, {
                name_en: cert.name_en,
                name_es: cert.name_es,
                issuer_en: cert.issuer_en,
                issuer_es: cert.issuer_es
              });
            } else {
              addLog('warning', `   ⚠️ Certificación ${index + 1} sin ID válido`);
            }
          });
        } else {
          addLog('warning', `   ⚠️ Documento ${doc.id} no tiene certificaciones válidas`);
        }
      });
      
      addLog('success', `📊 RESUMEN DE DATOS DE REFERENCIA:`);
      addLog('success', `   Total de certificaciones cargadas: ${Object.keys(certificationData).length}`);
      
      // Mostrar algunas certificaciones de ejemplo
      const sampleCerts = Object.entries(certificationData).slice(0, 3);
      sampleCerts.forEach(([id, cert]: [string, any]) => {
        addLog('info', `   📋 Ejemplo - ID: ${id}`, undefined, undefined, {
          name_en: cert.name_en,
          name_es: cert.name_es
        });
      });
      
      // Obtener todos los usuarios
      addLog('info', 'Iniciando migración de certificaciones...');
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.certifications && Array.isArray(data.certifications) && data.certifications.length > 0;
      });

      setProgress({ current: 0, total: users.length });
      addLog('info', `Encontrados ${users.length} usuarios con certificaciones para procesar`);

      let processed = 0;
      let updated = 0;
      let errors = 0;

      for (const userDoc of users) {
        try {
          const userData = userDoc.data();
          addLog('info', `Procesando usuario: ${userData.firstName} ${userData.lastName} (${userDoc.id})`, userDoc.id);

          const updatedCertifications = userData.certifications.map((cert: any, index: number) => {
            addLog('info', `📋 Procesando certificación ${index + 1}/${userData.certifications.length}`, userDoc.id);
            addLog('info', `   ID: ${cert.certificationId}`, userDoc.id);
            addLog('info', `   Nombre actual: "${cert.name}"`, userDoc.id);
            addLog('info', `   Emisor actual: "${cert.issuer}"`, userDoc.id);
            
            // Buscar datos de referencia para esta certificación
            const referenceData = certificationData[cert.certificationId];
            
            if (referenceData) {
              addLog('success', `   ✅ Encontrados datos de referencia para ${cert.certificationId}`, userDoc.id);
              addLog('info', `   📖 Referencia - name_en: "${referenceData.name_en}"`, userDoc.id);
              addLog('info', `   📖 Referencia - name_es: "${referenceData.name_es}"`, userDoc.id);
              addLog('info', `   📖 Referencia - issuer_en: "${referenceData.issuer_en}"`, userDoc.id);
              addLog('info', `   📖 Referencia - issuer_es: "${referenceData.issuer_es}"`, userDoc.id);
              
              const updatedCert = {
                ...cert,
                name_en: referenceData.name_en || cert.name || 'Unknown Certification',
                name_es: referenceData.name_es || cert.name || 'Certificación Desconocida',
                issuer_en: referenceData.issuer_en || cert.issuer || 'Unknown Issuer',
                issuer_es: referenceData.issuer_es || cert.issuer || 'Emisor Desconocido',
                description_en: referenceData.description_en || cert.description || '',
                description_es: referenceData.description_es || cert.description || '',
                duration_en: referenceData.duration_en || cert.duration || '',
                duration_es: referenceData.duration_es || cert.duration || ''
              };
              
              addLog('success', `   ✨ CERTIFICACIÓN ACTUALIZADA:`, userDoc.id);
              addLog('success', `      name_en: "${updatedCert.name_en}"`, userDoc.id);
              addLog('success', `      name_es: "${updatedCert.name_es}"`, userDoc.id);
              addLog('success', `      issuer_en: "${updatedCert.issuer_en}"`, userDoc.id);
              addLog('success', `      issuer_es: "${updatedCert.issuer_es}"`, userDoc.id);
              
              return updatedCert;
            } else {
              addLog('warning', `   ⚠️ No se encontraron datos de referencia para ${cert.certificationId}`, userDoc.id);
              
              // Si no hay datos de referencia, usar los datos existentes
              if (!cert.name_en || !cert.name_es) {
                addLog('info', `   🔧 Aplicando fallback para certificación sin campos multilenguaje`, userDoc.id);
                
                const fallbackCert = {
                  ...cert,
                  name_en: cert.name || 'Unknown Certification',
                  name_es: cert.name || 'Certificación Desconocida',
                  issuer_en: cert.issuer || 'Unknown Issuer',
                  issuer_es: cert.issuer || 'Emisor Desconocido',
                  description_en: cert.description || '',
                  description_es: cert.description || '',
                  duration_en: cert.duration || '',
                  duration_es: cert.duration || ''
                };
                
                addLog('info', `   📝 FALLBACK APLICADO:`, userDoc.id);
                addLog('info', `      name_en: "${fallbackCert.name_en}"`, userDoc.id);
                addLog('info', `      name_es: "${fallbackCert.name_es}"`, userDoc.id);
                
                return fallbackCert;
              } else {
                addLog('info', `   ℹ️ Certificación ya tiene campos multilenguaje, sin cambios necesarios`, userDoc.id);
                addLog('info', `      name_en: "${cert.name_en}"`, userDoc.id);
                addLog('info', `      name_es: "${cert.name_es}"`, userDoc.id);
              }
            }
            return cert;
          });

          // Verificar si hubo cambios
          const hasChanges = JSON.stringify(userData.certifications) !== JSON.stringify(updatedCertifications);
          
          if (hasChanges) {
            await updateDoc(doc(db, 'users', userDoc.id), {
              certifications: updatedCertifications
            });
            updated++;
            addLog('success', `✅ Usuario actualizado: ${userData.firstName} ${userData.lastName} - ${updatedCertifications.length} certificaciones`, userDoc.id, updatedCertifications.length);
            
            // Mostrar resumen de cambios
            addLog('info', `📋 RESUMEN DE CAMBIOS PARA ${userData.firstName} ${userData.lastName}:`, userDoc.id);
            updatedCertifications.forEach((cert: any, index: number) => {
              addLog('info', `   Certificación ${index + 1} (${cert.certificationId}):`, userDoc.id, undefined, {
                name_en: cert.name_en,
                name_es: cert.name_es,
                issuer_en: cert.issuer_en,
                issuer_es: cert.issuer_es
              });
            });
          } else {
            addLog('info', `ℹ️ Usuario sin cambios: ${userData.firstName} ${userData.lastName}`, userDoc.id);
          }

          processed++;
          setProgress(prev => ({ ...prev, current: processed }));
          setStats({ processed, updated, errors });

          // Pequeña pausa para no sobrecargar Firestore
          await new Promise(resolve => setTimeout(resolve, 100));

        } catch (error) {
          errors++;
          addLog('error', `❌ Error procesando usuario ${userDoc.id}: ${error}`, userDoc.id);
          setStats({ processed, updated, errors });
        }
      }

      addLog('success', `🎉 Migración completada! Procesados: ${processed}, Actualizados: ${updated}, Errores: ${errors}`);
      
    } catch (error) {
      addLog('error', `❌ Error general en la migración: ${error}`);
    } finally {
      setIsMigrating(false);
    }
  };

  const getLogIcon = (type: MigrationLog['type']) => {
    switch (type) {
      case 'success': return <FaCheck className="text-green-500" />;
      case 'error': return <FaTimes className="text-red-500" />;
      case 'warning': return <FaExclamationTriangle className="text-yellow-500" />;
      default: return <FaSync className="text-blue-500" />;
    }
  };

  const getLogColor = (type: MigrationLog['type']) => {
    switch (type) {
      case 'success': return 'bg-green-50 border-green-200';
      case 'error': return 'bg-red-50 border-red-200';
      case 'warning': return 'bg-yellow-50 border-yellow-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <FaArrowLeft className="text-xl" />
            </button>
            <h1 className="text-3xl font-bold text-gray-800">Migración de Certificaciones</h1>
          </div>
          
          <p className="text-gray-600 mb-6">
            Esta herramienta actualiza las certificaciones existentes para incluir campos en ambos idiomas (inglés y español).
            Las nuevas certificaciones se guardarán automáticamente con todos los campos necesarios.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.processed}</div>
              <div className="text-sm text-blue-700">Usuarios Procesados</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="text-2xl font-bold text-green-600">{stats.updated}</div>
              <div className="text-sm text-green-700">Usuarios Actualizados</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="text-2xl font-bold text-red-600">{stats.errors}</div>
              <div className="text-sm text-red-700">Errores</div>
            </div>
          </div>

          {progress.total > 0 && (
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progreso: {progress.current} / {progress.total}</span>
                <span>{Math.round((progress.current / progress.total) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          <button
            onClick={startMigration}
            disabled={isMigrating}
            className={`px-8 py-4 rounded-xl font-semibold text-white transition-all duration-200 flex items-center gap-3 ${
              isMigrating 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 hover:scale-105'
            }`}
          >
            <FaSync className={`text-xl ${isMigrating ? 'animate-spin' : ''}`} />
            {isMigrating ? 'Migrando...' : 'Iniciar Migración'}
          </button>
        </div>

        {/* Logs */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Log de Migración</h2>
          
          {logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FaSync className="text-4xl mx-auto mb-4 text-gray-300" />
              <p>No hay logs de migración. Haz clic en "Iniciar Migración" para comenzar.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {logs.map((log, index) => (
                <div 
                  key={index} 
                  className={`p-4 rounded-xl border ${getLogColor(log.type)} flex items-start gap-3`}
                >
                  <div className="flex-shrink-0 mt-1">
                    {getLogIcon(log.type)}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-800">
                      {log.message}
                    </div>
                    {log.userId && (
                      <div className="text-xs text-gray-500 mt-1">
                        User ID: {log.userId}
                        {log.certificationCount && ` | Certificaciones: ${log.certificationCount}`}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      {log.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificationMigration; 